/**
 * Quantity Picker
 *
 * Visual wrapper for a product option (e.g. Quantity) that drives the native
 * hidden quantity radios inside the variant picker form. The variant picker owns
 * all state management — variant resolution, section fetching, morphing, and
 * event dispatching. This component only handles card UI and syncs from
 * ThemeEvents.variantChanged.
 *
 * Dispatches:
 *  - ThemeEvents.variantSelected — sticky add-to-cart title update
 *    (needs the full variant object; ThemeEvents.variantChanged provides it)
 */
import { ThemeEvents } from "@theme/events";

class QuantityPicker {
  #root;
  #variants;
  #optionPosition;

  constructor(root) {
    this.#root = root;
    this.#optionPosition = Number(root.dataset.optionPosition);

    const variantsEl = root.querySelector("[data-qp-variants]");
    this.#variants = JSON.parse(variantsEl?.textContent || "[]");

    this.#bindCards();
    this.#bindVariantChange();
    this.#initAvailability();
  }

  #bindCards() {
    this.#root.addEventListener("change", (e) => {
      const radio = e.target.closest("[data-qp-radio]");
      if (radio) this.#onSelect(radio);
    });
  }

  /**
   * On quantity card selection: update active/label immediately for
   * responsiveness, then drive the native (hidden) variant picker radio.
   * The variant picker handles variant resolution, section fetch, morph,
   * and dispatches variantChanged/variantSelected automatically.
   */
  #onSelect(radio) {
    const selectedValueId = radio.dataset.optionValueId;
    this.#root.querySelectorAll("[data-qp-card]").forEach((card) => {
      card.classList.toggle("is-active", card.dataset.optionValueId === selectedValueId);
    });

    const labelEl = this.#root.querySelector("[data-qp-label-value]");
    if (labelEl) labelEl.textContent = radio.dataset.optionValue;

    this.#triggerNativeRadio(radio.dataset.optionValue);
  }

  /**
   * Checks the matching native quantity radio inside the variant picker form
   * and dispatches a change event on it. The variant picker's own change
   * handler fires, resolves the full variant, fetches/morphs the section,
   * and dispatches ThemeEvents.variantChanged.
   */
  #triggerNativeRadio(optionValue) {
    const productId = this.#root.dataset.productId;
    const variantPicker = document.querySelector(
      `variant-picker[data-product-id="${productId}"]`
    );
    const nativeRadios = variantPicker?.querySelectorAll(
      "[data-qp-native-quantity] input[type='radio']"
    );
    const target = Array.from(nativeRadios || []).find(
      (radio) => radio.value === optionValue
    );

    if (target) {
      target.checked = true;
      target.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  #bindVariantChange() {
    document.addEventListener(ThemeEvents.variantChanged, ({ detail: { variant } }) => {
      const didAutoSwitch = this.#syncFromVariant(variant);

      // Forward a variantSelected event with the full variant object so the
      // sticky add-to-cart title updater works. Only dispatch if no auto-switch
      // happened — the corrected combination fires its own variantChanged which
      // will dispatch variantSelected with the right data.
      if (!didAutoSwitch) {
        this.#root.dispatchEvent(
          new CustomEvent(ThemeEvents.variantSelected, {
            bubbles: true,
            detail: { resource: variant },
          })
        );
      }
    });
  }

  /**
   * Runs visual sync once on page load from the initially selected variant,
   * so availability and active state are correct from the start (e.g. when
   * loading with a ?variant= URL param).
   */
  #initAvailability() {
    const productId = this.#root.dataset.productId;
    const variantPicker = document.querySelector(
      `variant-picker[data-product-id="${productId}"]`
    );
    if (!variantPicker) return;

    const selectedOptions = {};

    variantPicker.querySelectorAll("input[type='radio']:checked").forEach((input) => {
      const position = Number(input.dataset.inputId?.split("-")[0]);
      if (position) selectedOptions[position] = input.value;
    });

    if (!Object.keys(selectedOptions).length) return;

    const initialVariant = this.#variants.find((variant) =>
      Object.entries(selectedOptions).every(([pos, val]) => variant[`option${pos}`] === val)
    );

    if (initialVariant) this.#syncFromVariant(initialVariant);
  }

  /**
   * Syncs all card visual states (is-active, is-unavailable, label) from a
   * variant object. Availability is checked against the variant's other option
   * values (e.g. flavour) so each quantity card reflects whether that
   * combination exists and has inventory.
   *
   * If the currently active quantity is unavailable for the incoming variant's
   * other options (e.g. user switched to a flavour not available in 3 Boxes),
   * auto-selects the first available quantity by triggering its native radio.
   *
   * Returns true if an auto-switch was triggered (caller should skip
   * dispatching variantSelected for the stale combination).
   *
   * @param {object} variant  Full Shopify variant object
   * @returns {boolean}
   */
  #syncFromVariant(variant) {
    const currentQuantityValue = variant[`option${this.#optionPosition}`];

    // Other option values (e.g. flavour) used for per-card availability checks
    const otherOptions = {};
    for (let i = 1; i <= 3; i++) {
      if (i !== this.#optionPosition && variant[`option${i}`] != null) {
        otherOptions[i] = variant[`option${i}`];
      }
    }

    let activeCardAvailable = true;
    let firstAvailableValue = null;

    this.#root.querySelectorAll("[data-qp-card]").forEach((card) => {
      const radio = card.querySelector("[data-qp-radio]");
      if (!radio) return;

      const quantityValue = radio.dataset.optionValue;
      const isActive = quantityValue === currentQuantityValue;

      const available = this.#variants.some(
        (v) =>
          v[`option${this.#optionPosition}`] === quantityValue &&
          Object.entries(otherOptions).every(([pos, val]) => v[`option${pos}`] === val) &&
          v.available
      );

      card.classList.toggle("is-active", isActive);
      card.classList.toggle("is-unavailable", !available);
      radio.setAttribute("aria-disabled", String(!available));

      if (isActive && !available) activeCardAvailable = false;
      if (available && !firstAvailableValue) firstAvailableValue = quantityValue;
    });

    // Update label to match the resolved quantity value
    if (currentQuantityValue) {
      const labelEl = this.#root.querySelector("[data-qp-label-value]");
      if (labelEl) labelEl.textContent = currentQuantityValue;
    }

    // Auto-switch: trigger native radio for first available quantity.
    if (!activeCardAvailable && firstAvailableValue) {
      queueMicrotask(() => this.#triggerNativeRadio(firstAvailableValue));
      return true;
    }

    return false;
  }
}

document.querySelectorAll("[data-qp]").forEach((root) => new QuantityPicker(root));